import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { FfdNotesInvitationDataService, Profile, ProfileDataService } from '@ffd/api-client';
import { FfdComponentStore } from '@libs/utils';
import { catchError, EMPTY, filter, finalize, switchMap, take, tap } from 'rxjs';
import { authActions, selectCurrentUserProfile, selectHasFullAccessAtAnySite } from '@libs/store';
import { ToastService } from '@libs/shared-services';
import { Clipboard } from '@angular/cdk/clipboard';
import { TranslateService } from '@ngx-translate/core';

export interface MyAccountState {
  profile: Profile | null;
  isInviteLinkLoading: boolean;
}

export interface MyAccountVM {
  profile: Profile | null;
  isInviteLinkLoading: boolean;
}

@Injectable()
export class MyAccountScreenService extends FfdComponentStore<MyAccountState, MyAccountVM> {
  private readonly store = inject(Store);
  private readonly toastService = inject(ToastService);
  private readonly profileDataService = inject(ProfileDataService);
  private readonly ffdNotesInvitationDataService = inject(FfdNotesInvitationDataService);
  private readonly clipboard = inject(Clipboard);
  private readonly translateService = inject(TranslateService);

  public constructor() {
    super({
      profile: null,
      isInviteLinkLoading: false,
    });
  }

  public override init(): void {
    this.getUser();
    this.loadEaglesoftCredentialStatuses();
  }

  public updateProfileName = this.effect<{ firstName: string; lastName: string }>(($) =>
    $.pipe(
      // Mock backend call for now - replace with actual API call later
      switchMap(({ firstName, lastName }) => {
        return this.profileDataService.updateProfile({ firstName, lastName }).pipe(
          tap(() => {
            // Update local state with new name
            const currentProfile = this.get().profile;
            if (currentProfile) {
              this.patchState({
                profile: {
                  ...currentProfile,
                  firstName,
                  lastName,
                },
              });
            }
            this.toastService.success('Account name updated.');
          }),
        );
      }),
    ),
  );

  private readonly getUser = this.effect(($) =>
    $.pipe(
      switchMap(() => {
        return this.store.select(selectCurrentUserProfile).pipe(
          filter((profile): profile is Profile => !!profile),
          tap((profile) => {
            this.patchState({ profile });
          }),
        );
      }),
    ),
  );

  // The screen lists the Eaglesoft password state of every site, not only the selected one
  private readonly loadEaglesoftCredentialStatuses = this.effect(($) =>
    $.pipe(
      switchMap(() =>
        this.store.select(selectCurrentUserProfile).pipe(
          filter((profile): profile is Profile => !!profile),
          take(1),
          switchMap(() => this.store.select(selectHasFullAccessAtAnySite).pipe(take(1))),
          filter((hasFullAccess) => hasFullAccess),
          tap(() => this.store.dispatch(authActions.getEaglesoftCredentialStatuses())),
        ),
      ),
    ),
  );

  public shareInviteLink = this.effect<string>(($) =>
    $.pipe(
      tap(() => this.patchState({ isInviteLinkLoading: true })),
      switchMap((siteId) =>
        this.ffdNotesInvitationDataService.createOrExtendFfdNotesInvitationLink(siteId).pipe(
          tap((response) => {
            this.clipboard.copy(response.invitationUrl);
            this.toastService.success(this.translateService.instant('express_account_baa_section_invite_link_copied_toast'));
          }),
          catchError(() => {
            this.toastService.error(this.translateService.instant('common_error_message'));
            return EMPTY;
          }),
          finalize(() => this.patchState({ isInviteLinkLoading: false })),
        ),
      ),
    ),
  );

  protected override mapStateToViewModel(state: MyAccountState): MyAccountVM {
    return {
      profile: state.profile,
      isInviteLinkLoading: state.isInviteLinkLoading,
    };
  }
}
